import { createReducer, on } from '@ngrx/store';

import { isCartHydrationBlocked } from '../../utils/cart-hydration.util';

import * as CartActions from './cart.actions';
import { 
  initialState, 
  CartItem, 
  createCartItem,
  findCartItemById,
} from './cart.state';

const CART_STORAGE_KEY = 'umarketplace_cart_items';
const CART_BACKUP_KEY = 'umarketplace_cart_backup';
const CART_METADATA_KEY = 'umarketplace_cart_metadata';

const clearCartBrowserStorage = (): void => {
  try {
    localStorage.removeItem(CART_STORAGE_KEY);
    localStorage.removeItem(CART_BACKUP_KEY);
    localStorage.removeItem(CART_METADATA_KEY);
  } catch (error) {
    console.error('Erro ao limpar carrinho no localStorage:', error);
  }
};

const saveToLocalStorage = (items: CartItem[]) => {
  try {
    if (isCartHydrationBlocked() || items.length === 0) {
      clearCartBrowserStorage();
      return;
    }

    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  } catch (error) {
    console.error('Erro ao salvar carrinho no localStorage:', error);
  }
};

function mergeItems(localItems: CartItem[], remoteItems: CartItem[]): CartItem[] {
  const mergedMap = new Map<string, CartItem>();
  
  for (const remoteItem of remoteItems) {
    mergedMap.set(remoteItem.id, { ...remoteItem });
  }
  
  for (const localItem of localItems) {
    if (!mergedMap.has(localItem.id)) {
      mergedMap.set(localItem.id, { ...localItem });
    }
  }
  
  return Array.from(mergedMap.values());
}

function updateItemInArray(items: CartItem[], itemId: string, updates: Partial<CartItem>): CartItem[] {
  return items.map(item => 
    item.id === itemId 
      ? { ...item, ...updates, lastModified: Date.now() }
      : item
  );
}

function removeItemFromArray(items: CartItem[], itemId: string): CartItem[] {
  return items.filter(item => item.id !== itemId);
}

export const cartReducer = createReducer(
  initialState,

  on(CartActions.addItem, (state, { product, quantity, selectedVariation }) => {
    const newItem = createCartItem(product, quantity, selectedVariation);
    const existingItem = findCartItemById(state.items, newItem.id);
    
    let updatedItems: CartItem[];
    if (existingItem) {
      updatedItems = updateItemInArray(state.items, newItem.id, {
        quantity: existingItem.quantity + quantity
      });
    } else {
      updatedItems = [...state.items, newItem];
    }

    saveToLocalStorage(updatedItems);
    
    return {
      ...state,
      items: updatedItems,
      ui: { ...state.ui, loading: false },
      error: null,
      lastUpdated: Date.now()
    };
  }),

  on(CartActions.removeItem, (state, { itemId }) => {
    const updatedItems = removeItemFromArray(state.items, itemId);
    saveToLocalStorage(updatedItems);
    
    return {
      ...state,
      items: updatedItems,
      ui: { ...state.ui, loading: false },
      error: null,
      lastUpdated: Date.now()
    };
  }),

  on(CartActions.updateQuantity, (state, { itemId, quantity }) => {
    if (quantity <= 0) {
      return {
        ...state,
        error: 'Quantidade deve ser maior que zero',
        lastUpdated: Date.now()
      };
    }
    
    const updatedItems = updateItemInArray(state.items, itemId, { quantity });
    saveToLocalStorage(updatedItems);
    
    return {
      ...state,
      items: updatedItems,
      ui: { ...state.ui, loading: false },
      error: null,
      lastUpdated: Date.now()
    };
  }),

  on(CartActions.updateProduct, (state, { itemId, product }) => {
    const updatedItems = updateItemInArray(state.items, itemId, {
      product,
      productType: product?.productType
    });
    saveToLocalStorage(updatedItems);
    
    return {
      ...state,
      items: updatedItems,
      ui: { ...state.ui, loading: false },
      error: null,
      lastUpdated: Date.now()
    };
  }),

  on(CartActions.updateCartItem, (state, { itemId, updates }) => {
    const updatedItems = updateItemInArray(state.items, itemId, updates);
    saveToLocalStorage(updatedItems);
    
    return {
      ...state,
      items: updatedItems,
      ui: { ...state.ui, loading: false },
      error: null,
      lastUpdated: Date.now()
    };
  }),

  on(CartActions.clearCart, CartActions.clearLocalCart, (state) => {
    saveToLocalStorage([]);
    
    return {
      ...state,
      items: [],
      ui: { ...state.ui, loading: false },
      error: null,
      lastUpdated: Date.now()
    };
  }),

  on(CartActions.loadCartSuccess, (state, { items, source }) => {
    let mergedItems: CartItem[];
    
    if (source === 'backend') {
      mergedItems = mergeItems(state.items, items);
    } else {
      mergedItems = items;
    }
    
    saveToLocalStorage(mergedItems);
    
    return {
      ...state,
      items: mergedItems,
      ui: { ...state.ui, loading: false },
      error: null,
      lastUpdated: Date.now()
    };
  }),

  on(CartActions.syncCart, (state) => ({
    ...state,
    sync: {
      ...state.sync,
      inProgress: true,
      lastError: null
    }
  })),

  on(CartActions.operationSuccess, (state) => ({
    ...state,
    ui: { ...state.ui, loading: false },
    sync: {
      ...state.sync,
      inProgress: false,
      lastSync: Date.now(),
      retryCount: 0
    },
    error: null,
    lastUpdated: Date.now()
  })),

  on(CartActions.operationFailure, (state, { error, context }) => ({
    ...state,
    ui: { ...state.ui, loading: false },
    sync: {
      ...state.sync,
      inProgress: false,
      lastError: error,
      retryCount: state.sync.retryCount + 1
    },
    error,
    lastUpdated: Date.now()
  })),

  on(CartActions.setLoading, (state, { loading }) => ({
    ...state,
    ui: { ...state.ui, loading }
  })),

  on(CartActions.setUrlCartLoading, (state, { loading }) => ({
    ...state,
    ui: { ...state.ui, urlCartLoading: loading }
  })),

  on(CartActions.openCartSidebar, (state) => ({
    ...state,
    ui: { ...state.ui, isSidebarOpen: true }
  })),

  on(CartActions.closeCartSidebar, (state) => ({
    ...state,
    ui: { ...state.ui, isSidebarOpen: false }
  })),

  on(CartActions.setSidebarHeader, (state, { icon, customIcon, title }) => ({
    ...state,
    ui: {
      ...state.ui,
      sidebarHeader: { icon, customIcon, title }
    }
  })),

);
